import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import logo from '../assets/images/logo-navbar.png';
import LanguagePicker from '../components/LanguagePicker';
import { Menu, X } from 'lucide-react';

const HeaderWrapper = styled.header`
  background: transparent;
  padding-left: 2rem;
  padding-right: 4rem;
  padding-top: 0rem;
  padding-bottom: 0rem;
  @media (max-width: 768px) {
    padding-left: 1rem;
    padding-right: 1rem;
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled(Link)`
  height: 75px;
  margin-left: 10px;
  
  @media (max-width: 768px) {
    height: 60px;
    margin-left: 0;
  }
`;

const LogoImage = styled.img`
  height: 100%;
  object-fit: contain;
`;

const SignInWrapper = styled.div`
  position: relative;
`;

const SignInTrigger = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &::after {
    content: '.';
    color: #47ABDB;
    margin-left: 2px;
  }
`;

const AccountDropdown = styled.div`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  width: 12rem;
  background: white;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  padding: 0.25rem 0;
  z-index: 100;
`;

const AccountOption = styled.a`
  display: block;
  padding: 0.5rem 1rem;
  color: #374151;
  text-decoration: none;
  font-size: 0.95rem;

  &:hover {
    background: #f3f4f6;
  }
`;

const AccountOptionTitle = styled.div`
  font-weight: bold;
`;

const AccountOptionNote = styled.div`
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.15rem;
`;

const NavLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  @media (max-width: 1180px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  
  @media (max-width: 1180px) {
    display: block;
    margin-left: 1rem;
  }
`;

const MobileMenu = styled.div`
  display: none;
  position: absolute;
  top: 90px;
  left: 0;
  right: 0;
  background: #90C4D9;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  z-index: 50;
  gap: 1rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  &.open {
    display: flex;
  }
`;

const Divider = styled.div`
  width: 90%;
  border-top: 1px solid white;
  margin: 0.5rem 0;
`;

const MobileAuthLink = styled.a`
  display: block;
  color: white;
  text-decoration: none;
  font-weight: bold;
  font-size: 1.125rem;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`;

const MobileAuthTitle = styled.span`
  &::after {
    content: '.';
    color: #47ABDB;
    margin-left: 2px;
  }
`;

const MobileAuthNote = styled.span`
  display: block;
  font-weight: normal;
  font-size: 0.8rem;
  opacity: 0.9;
  margin-top: 0.15rem;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
`;

const NavLinkStyled = styled.div`
  position: relative;
  font-weight: bold;
  font-size: 1.125rem;
  
  a {
    color: white;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  &::after {
    content: ".";
    color: #47ABDB;
    margin-left: 2px;
  }
`;

const LanguagePickerWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: 2rem;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Separator = styled.span`
  color: white;
  font-weight: bold;
  font-size: 1.2rem;
`;

const AuthSection = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const accountOptions = [
  {
    titleId: 'header.expertAccount',
    titleDefault: 'Expert Account',
    noteId: 'header.expertAccountNote',
    noteDefault: 'For Expert & Conference',
    href: 'https://conference.heartport.org/login',
  },
  {
    titleId: 'header.publicAccount',
    titleDefault: 'Public Account',
    noteId: 'header.publicAccountNote',
    noteDefault: 'For HEARTPORT Public',
    href: 'https://public.heartport.org/login',
  },
];

const SignInMenu = () => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const signInLabel = intl.formatMessage({ id: 'header.signIn', defaultMessage: 'Sign in' });

  return (
    <SignInWrapper ref={wrapperRef}>
      <SignInTrigger
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={signInLabel}
      >
        {signInLabel}
      </SignInTrigger>

      {isOpen && (
        <AccountDropdown role="menu">
          {accountOptions.map((option) => (
            <AccountOption
              key={option.href}
              href={option.href}
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={() => setIsOpen(false)}
            >
              <AccountOptionTitle>
                {intl.formatMessage({ id: option.titleId, defaultMessage: option.titleDefault })}
              </AccountOptionTitle>
              <AccountOptionNote>
                {intl.formatMessage({ id: option.noteId, defaultMessage: option.noteDefault })}
              </AccountOptionNote>
            </AccountOption>
          ))}
        </AccountDropdown>
      )}
    </SignInWrapper>
  );
};

const Header = () => {
  const intl = useIntl();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const t = (id, defaultMessage) => intl.formatMessage({ id, defaultMessage });

  const ExternalLink = ({ to, children }) => {
    return (
      <NavLinkStyled>
        <a href={to} target="_blank" rel="noopener noreferrer">{children}</a>
      </NavLinkStyled>
    );
  };
  
  return (
    <HeaderWrapper>
      <Nav>
        <Logo to="/">
          <LogoImage src={logo} alt="HEARTS Logo" />
        </Logo>

        <RightSection>
          <NavLinkContainer>
            <ExternalLink to="https://public.heartport.org">HeartPort Public</ExternalLink>
            <ExternalLink to="https://expert.heartport.org">HeartPort Expert</ExternalLink>
            <ExternalLink to="https://conference.heartport.org">Hearts Conference</ExternalLink>
            <ExternalLink to="https://public.heartport.org/crew">Crew</ExternalLink>
          </NavLinkContainer>

          <LanguagePickerWrapper>
            <Separator>|</Separator>
            <LanguagePicker />
            <Separator>|</Separator>
          </LanguagePickerWrapper>

          <AuthSection>
            <SignInMenu />
          </AuthSection>

          <MobileMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </MobileMenuButton>
        </RightSection>
      </Nav>
      
      <MobileMenu className={isMenuOpen ? 'open' : ''}>
        <ExternalLink to="https://public.heartport.org">HeartPort Public</ExternalLink>
        <ExternalLink to="https://expert.heartport.org">HeartPort Expert</ExternalLink>
        <ExternalLink to="https://conference.heartport.org">Hearts Conference</ExternalLink>
        <ExternalLink to="https://public.heartport.org/crew">Crew</ExternalLink>
        <Divider />
        {accountOptions.map((option) => (
          <MobileAuthLink
            key={option.href}
            href={option.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setIsMenuOpen(false)}
          >
            <MobileAuthTitle>
              {t(option.titleId, option.titleDefault)}
            </MobileAuthTitle>
            <MobileAuthNote>
              {t(option.noteId, option.noteDefault)}
            </MobileAuthNote>
          </MobileAuthLink>
        ))}
      </MobileMenu>
    </HeaderWrapper>
  );
};

export default Header;